import { ItemImage } from '@components/CatalogElements';
import { MarketplaceIcon } from '@components/ConnectedMarketplaces';
import { Flag, flagImages } from '@elements/Flag';
import { ParentImages } from '@page-components/catalog';
import { OrderTime } from '@page-components/order';
import { POStatusBox } from '@page-components/purchase-order';
import { CommonUtility } from '@utility/common';
import {
  CommonConstant,
  InvoiceStatusReadable,
  OrderStatus,
} from '@utility/constants/common';
import { useNavigate } from 'react-router';
import {
  Factory,
  Package,
  Scroll,
  ShoppingBag,
  Storefront,
  Wrench,
} from '@phosphor-icons/react';
import { Image } from '@skupreme/shared/ui';

const title = {
  component: 'Catalogs Components',
  customer: 'Customers',
  fulfillmentProvider: 'Storage Provider',
  items: 'Catalog',
  order: 'Orders',
  purchaseOrder: 'Purchase Orders',
  shipment: 'Shipments',
  supplier: 'Supplier',
  wholesaler: 'Wholesaler',
  wholesalerInvoice: 'Wholesaler Invoices',
  workOrder: 'Work Orders',
};

const ICON = 'sk:shrink-0';

/* One clickable result: a rule under each, a fill on hover. */
const ROW =
  'sk:cursor-pointer sk:border-b sk:border-rule sk:px-3 sk:py-2.5 sk:text-xs sk:leading-normal sk:text-fg sk:hover:bg-fill-tertiary';

/**
 * Renders highlighted text based on a search query within a given text.
 * @param {Object} props - The props for the component.
 * @param {string} props.text - The text to be displayed.
 * @param {string} props.search - The search query to highlight within the text.
 * @param {string} [props.className] - Additional class name for styling.
 * @returns {JSX.Element} - React component to render highlighted text.
 */
function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function highlightSegments(word: string, searchTerms: string[]) {
  const term = searchTerms.find(t => t && word.toLowerCase().includes(t));
  if (!term) return [word];

  const regex = new RegExp(`(${escapeRegExp(term)})`, 'gi');
  return word.split(regex);
}

function RenderHighlightedText(props) {
  const { text, search, className = '' } = props;

  const searchTerms = search
    .split(' ')
    .map((d: string) => d.toLowerCase())
    .filter(Boolean);

  const words = text.split(' ');

  return (
    <span className={className}>
      {words.map((word, wi) => {
        const segments = highlightSegments(word, searchTerms);
        const isExactMatch = searchTerms.includes(word.toLowerCase());

        return (
          <span key={wi}>
            {isExactMatch ? (
              <strong>{word}</strong>
            ) : (
              segments.map((seg, si) =>
                searchTerms.includes(seg.toLowerCase()) ? (
                  <strong key={si}>{seg}</strong>
                ) : (
                  <span key={si}>{seg}</span>
                ),
              )
            )}
            {wi < words.length - 1 ? ' ' : ''}
          </span>
        );
      })}
    </span>
  );
}

function SingleResult(props) {
  const { type, data: d, searchTerm } = props;

  const navigate = useNavigate();

  const handleClick = () => {
    if (!d?._id) {
      return false;
    }

    const links = {
      component: `/app/catalogs/components/${d._id}`,
      customer: `/app/crm/${d._id}`,
      fulfillmentProvider: `/app/scm/storage-provider/${d._id}`,
      items: `/app/catalogs/catalog/${d._id}`,
      order: `/app/orders/${d._id}`,
      purchaseOrder: `/app/scm/purchase-orders/${d._id}`,
      shipment: `/app/scm/shipments/${d._id}`,
      supplier: `/app/scm/supplier/${d._id}`,
      wholesaler: `/app/wholesale/wholesalers/${d._id}`,
      wholesalerInvoice: `/app/wholesale/invoices/${d._id}`,
      workOrder: `/app/scm/work-orders/${d._id}`,
    };

    if (links?.[type]) navigate(links[type]);
  };

  const renderImages = () => {
    if (type === 'component') {
      return (
        <Image
          size="sm"
          alt=""
          fit="contain"
          src={d?.images?.[0] || CommonConstant.defaultImage}
        />
      );
    }

    if (type === 'order' || type === 'items') {
      const isParent = !!d?.is_parent;

      return isParent ? (
        // @ts-expect-error - TS2739: Type '{ images: any; }' is missing the following properties from type '{ images:...
        <ParentImages images={d?.variation_images || []} />
      ) : (
        <ItemImage item={d} size="mini" />
      );
    }

    return null;
  };

  const renderIcon = () => {
    switch (type) {
      case 'fulfillmentProvider':
        return <Package size={16} className={ICON} />;
      case 'order':
        return <ShoppingBag size={16} className={ICON} />;
      case 'purchaseOrder':
        return <Scroll size={16} className={ICON} />;
      case 'supplier':
        return <Factory size={16} className={ICON} />;
      case 'wholesaler':
        return <Storefront size={16} className={ICON} />;
      case 'workOrder':
        return <Wrench size={16} className={ICON} />;
      default:
        return null;
    }
  };

  const renderTitle = () => {
    let title = '';
    if (type === 'shipment') {
      title = d?.display_id || '';
    } else {
      title =
        d?.display_id ||
        d?.name ||
        d?.title ||
        d?.wholesaler_name ||
        d?.marketplace_order_display_id ||
        d?.p_title ||
        '';
    }

    return (
      <RenderHighlightedText
        text={title}
        search={searchTerm}
        className="sk:min-w-0 sk:break-words"
      />
    );
  };

  const renderInfo = () => {
    if (type === 'purchaseOrder') {
      return (
        <>
          <div className="sk:col-span-4 sk:flex sk:items-center sk:justify-center sk:text-center">
            {d?.supplier_name && (
              <RenderHighlightedText
                text={d.supplier_name}
                search={searchTerm}
              />
            )}
          </div>
          <div className="sk:col-span-4 sk:flex sk:items-center sk:justify-end">
            {d?.status && (
              <POStatusBox
                status={d?.status}
                isPublic={undefined}
                title={undefined}
                className="sk:mr-0!"
              />
            )}
          </div>
        </>
      );
    }

    if (type === 'shipment' && d?.FBA_id) {
      return <RenderHighlightedText text={d.FBA_id} search={searchTerm} />;
    }

    if (type === 'supplier' && d?.email) {
      return <RenderHighlightedText text={d.email} search={searchTerm} />;
    }

    if (type === 'wholesaler' && d?.email) {
      return <RenderHighlightedText text={d.email} search={searchTerm} />;
    }

    if (type === 'component' && d?.sku) {
      return (
        <div className="sk:text-right">
          <RenderHighlightedText text={d.sku} search={searchTerm} />
        </div>
      );
    }

    if (type === 'customer' && CommonUtility.isValidObject(d?.address)) {
      return (
        <div className="sk:text-right">
          <RenderHighlightedText
            text={CommonUtility.formattedAddress(d?.address)}
            search={searchTerm}
          />
        </div>
      );
    }

    if (
      type === 'customer' &&
      !CommonUtility.isValidObject(d?.address) &&
      d?.addressLine1
    ) {
      return (
        <div className="sk:text-right">
          <RenderHighlightedText
            text={CommonUtility.formattedAddress(d)}
            search={searchTerm}
          />
        </div>
      );
    }

    if (type === 'wholesalerInvoice' && d?.status) {
      return (
        <div className="sk:text-right">
          <RenderHighlightedText
            text={InvoiceStatusReadable?.[d.status] || d.status}
            search={searchTerm}
          />
        </div>
      );
    }

    if (type === 'items' && d?.p_sku) {
      return (
        <div className="sk:text-right sk:whitespace-nowrap">
          <RenderHighlightedText text={d.p_sku} search={searchTerm} />
        </div>
      );
    }

    if (type === 'order') {
      const marketplace = d?.marketplace?.[0] || null;

      return (
        <>
          <div className="sk:col-span-2">
            <OrderTime
              time={d.time_acknowledged}
              isEnabled={[
                OrderStatus.PendingShipment,
                OrderStatus.PendingFulfillment,
              ].includes(d?.state)}
            />
          </div>
          {marketplace?.type && (
            <div className="sk:col-span-1 sk:flex sk:justify-center">
              {/* @ts-expect-error - TS2739: Type '{ marketplace: any; extraClasses: string; fallbackOrg:... */}
              <MarketplaceIcon
                marketplace={marketplace}
                extraClasses="sk:m-auto"
                fallbackOrg={false}
              />
            </div>
          )}
          <div className="sk:col-span-3">{d?.fulfillment_status}</div>
          <div
            className={
              marketplace?.type
                ? 'sk:col-span-1 sk:text-right'
                : 'sk:col-span-2 sk:text-right'
            }
          >
            {
              // @ts-expect-error - ts-migration
              CommonUtility.currencyFormat(d?.amount, 'USD' || d.currency_code)
            }
          </div>
        </>
      );
    }
  };

  if (type === 'order' || type === 'purchaseOrder') {
    return (
      <div
        className={`${ROW} sk:grid sk:grid-cols-12 sk:items-center sk:gap-2`}
        onClick={handleClick}
      >
        <div
          className={`${
            type === 'order' ? 'sk:col-span-5' : 'sk:col-span-4'
          } sk:flex sk:min-w-0 sk:items-center sk:gap-1`}
        >
          {renderIcon()}
          {renderTitle()}
        </div>
        {renderInfo()}
      </div>
    );
  }

  return (
    <div
      className={`${ROW} sk:flex sk:items-center sk:justify-between sk:gap-1.5`}
      onClick={handleClick}
    >
      <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5">
        {renderImages()}
        {renderIcon()}
        {d?.country && flagImages[d.country?.toUpperCase()] && (
          <Flag name={d.country} className="sk:m-0" />
        )}
        {renderTitle()}
      </div>
      {renderInfo()}
    </div>
  );
}

function SearchResults(props) {
  const { type, data, searchTerm = '' } = props;

  if (!CommonUtility.isValidArray(data)) {
    return null;
  }

  return (
    <>
      <div className="sk:sticky sk:top-0 sk:z-1 sk:border-b sk:border-rule sk:bg-surface sk:px-3 sk:pt-2.5 sk:pb-1.5 sk:text-xs sk:font-semibold sk:text-fg-secondary">
        {title?.[type] || type}
      </div>
      {data.map(d => (
        <SingleResult
          // @ts-expect-error - TS18046: 'd' is of type 'unknown'.
          key={d._id}
          data={d}
          type={type}
          searchTerm={searchTerm}
        />
      ))}
    </>
  );
}

export default SearchResults;
